import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateDb } from "../services/updateDb";

// All mailing settings live in one document, so saves are sent one after another
let saveQueue = Promise.resolve();

/**
 * Persists `value` to the mailing settings while the user edits. `toBody(value, savedValue)` builds the
 * (partial) settings update; `getDelay(value, savedValue)` allows saving switches immediately.
 */
const useAutosave = (value, toBody, getDelay = () => 800) => {
  const [status, setStatus] = useState('saved');
  const [error, setError] = useState(null);
  const latest = useRef(value);
  const saved = useRef(value);
  const timer = useRef(null);
  const options = useRef({ toBody, getDelay });
  latest.current = value;
  options.current = { toBody, getDelay };

  const isSaved = () => JSON.stringify(latest.current) === JSON.stringify(saved.current);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    saveQueue = saveQueue.then(async () => {
      if (isSaved()) return;
      const next = latest.current;
      setStatus('saving');
      const res = await updateDb('/api/mailings/settings', options.current.toBody(next, saved.current), 'put');
      if (res.success) {
        saved.current = next;
        setError(null);
        setStatus(isSaved() ? 'saved' : 'pending');
      } else {
        setError(res.error);
        setStatus('error');
      }
    });
    return saveQueue;
  }, []);

  useEffect(() => {
    if (isSaved()) return;
    setStatus('pending');
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, options.current.getDelay(value, saved.current));
  }, [JSON.stringify(value)]);

  // Save pending edits when leaving the mailing page; warn when the browser tab is closed before saving
  useEffect(() => {
    const beforeUnload = (event) => {
      if (isSaved()) return;
      flush();
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      if (timer.current) flush();
    };
  }, []);

  return { status, error, flush };
};

export const SaveStatus = ({ status, error }) => {
  const { t } = useTranslation();
  if (status === 'error') {
    return <span className="mail-save-status error" title={error || ''}><i className="icon-alert" /> {t('mailing.autosave_error', { error: error || t('mailing.error') })}</span>;
  }
  const busy = status === 'pending' || status === 'saving';
  return (
    <span className={`mail-save-status ${busy ? 'busy' : 'saved'}`} aria-live="polite">
      <i className={busy ? 'icon-refresh' : 'icon-check-circle'} /> {t(busy ? 'mailing.autosave_saving' : 'mailing.autosave_saved')}
    </span>
  );
};

export default useAutosave;
